import { mockApiClient, realApiClient, mapApiListing, type CreateListingPayload } from "./apiClient";
import { calculatePacketReadiness } from "./packetService";
import { listingCreateSchema, type ListingCreateInput } from "../schemas/listingSchemas";
import type { AccountProfile } from "./authService";
import type { Bid, HoldRequest, Listing } from "../types/domain";

const listingDocumentsById = new Map<string, Array<{ type: "seller_license" | "dscsa_transaction_history" | "lot_expiry_attestation" | "shipping_sop"; status: "accepted" }>>([
  ["lst-ozempic", [
    { type: "seller_license", status: "accepted" },
    { type: "dscsa_transaction_history", status: "accepted" },
    { type: "lot_expiry_attestation", status: "accepted" },
    { type: "shipping_sop", status: "accepted" },
  ]],
]);

type IncomingBid = Bid & { sellerId: string; listing: Listing };
type IncomingHoldRequest = HoldRequest & { sellerId: string; listing: Listing };

export interface CreateListingFormInput {
  ndc: string;
  productName: string;
  strength: string;
  dosageForm: string;
  packageSize: string;
  manufacturer: string;
  lotNumber: string;
  expirationDate: string;
  quantityAvailable: string;
  unitPrice: string;
  holdWindowMinutes: string;
  shippingOriginState: string;
}

function listingForActivity(listingId: string) {
  return mockApiClient.getListing(listingId) ?? mockApiClient.listListings()[0];
}

function positiveInteger(value: string, field: string): number {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) throw new Error(`${field} must be a positive whole number`);
  return parsed;
}

function positiveNumber(value: string, field: string): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) throw new Error(`${field} must be a positive number`);
  return parsed;
}

export function buildCreateListingPayload(input: CreateListingFormInput, profile: AccountProfile): CreateListingPayload {
  const sellerOrgId = profile.primaryMembership?.orgId ?? profile.primaryOrganization?.orgId;
  if (!sellerOrgId) throw new Error("No seller organization is linked to this account.");
  if (!profile.permissions.includes("marketplace:sell")) throw new Error("This account does not have marketplace:sell permission.");

  return {
    sellerOrgId,
    createdByUserId: profile.userId,
    productName: input.productName.trim(),
    ndc: input.ndc.trim(),
    strength: input.strength.trim(),
    dosageForm: input.dosageForm.trim(),
    packageSize: input.packageSize.trim(),
    manufacturer: input.manufacturer.trim(),
    lotNumber: input.lotNumber.trim(),
    expirationDate: input.expirationDate,
    quantityAvailable: positiveInteger(input.quantityAvailable, "Quantity"),
    unitPrice: positiveNumber(input.unitPrice, "Unit price"),
    holdWindowMinutes: positiveInteger(input.holdWindowMinutes, "Hold window"),
    shippingOriginState: input.shippingOriginState.trim().toUpperCase(),
  };
}

export const sellerService = {
  listInventory: (sellerId: string) => mockApiClient.listListings().filter(listing => listing.sellerId === sellerId),
  createListing: (input: ListingCreateInput) => ({ id: `lst-${input.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`, ...listingCreateSchema.parse(input), status: "needs_packet_review" as const, packetStatus: "blocked" as const }),
  async createLiveListing(input: CreateListingFormInput, profile: AccountProfile): Promise<Listing> {
    return mapApiListing(await realApiClient.createListing(buildCreateListingPayload(input, profile)));
  },
  async activateLiveListing(listingId: string) {
    return realApiClient.activateListing(listingId);
  },
  calculatePacketReadiness: (listingId: string) => calculatePacketReadiness(listingDocumentsById.get(listingId) ?? []),
  listIncomingActivity: (sellerId: string): { bids: IncomingBid[]; holdRequests: IncomingHoldRequest[] } => {
    const bids = mockApiClient.listBids().flatMap(bid => {
      const listing = listingForActivity(bid.listingId);
      return listing.sellerId === sellerId ? [{ ...bid, sellerId: listing.sellerId, listing }] : [];
    });

    const holdRequests = mockApiClient.listHoldRequests().flatMap(holdRequest => {
      const listing = listingForActivity(holdRequest.listingId);
      return listing.sellerId === sellerId ? [{ ...holdRequest, sellerId: listing.sellerId, listing }] : [];
    });

    return { bids, holdRequests };
  },
};
